
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MaterialReactTable } from 'material-react-table';
import { 
  FiSearch, 
  FiPlus, 
  FiX,
  FiAlertTriangle,
  FiEye,
  FiFilter,
  FiUser,
  FiEdit3,
  FiTrash2,
  FiChevronDown,
  FiClipboard,
  FiFileText,
  FiActivity
} from 'react-icons/fi';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { TextField, IconButton } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PrescriptionService from '../../services/prescriptionPatientService';
import prescriptionService from '../../../../PrescriptionManagement/services/prescriptionService'; // Importar el servicio original
import TopHeader from "../../../../common/Header/TopHeader";
import PatientMedicalClinicalData from '../../../../Patient/listPrincipalComponent/TableComponent/PatientMedicalClinicalData';
import PatientManagement from '../../../../common/Menu/PatientManagement';
import PrescriptionForm from './PrescriptionForm';
// import ActionTableButton from '../Buttons/ActionTableButton';
import { API_BASE_HEALTH_CENTER_URL, API_HEALTH_CENTER_BY_ID } from '../../../../../helpers/urlHelper';

const PatientPrescriptionTreatments = ({ onCountUpdate, componentName = 'PatientPrescriptionTreatments' }) => {
  const location = useLocation();
  
  // **OBTENER DATOS DEL PACIENTE DESDE NAVIGATION STATE**
  const patientData = location.state;
  const patientId = patientData?.patientId;

  // Estados principales - usando la misma estructura que PrescriptionTable
  const [prescriptions, setPrescriptions] = useState([]); // Datos transformados para la tabla
  const [originalPrescriptions, setOriginalPrescriptions] = useState([]); // Datos originales de la API
  const [filteredPrescriptions, setFilteredPrescriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [alertInfo, setAlertInfo] = useState(null);
  const [activeFilters, setActiveFilters] = useState({});

  // Estados para offcanvas
  const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [showEditOffcanvas, setShowEditOffcanvas] = useState(false);
  const [editingPrescription, setEditingPrescription] = useState(null);
  const [showAddOffcanvas, setShowAddOffcanvas] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estados para edición inline - igual que PrescriptionTable
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  // Estados para confirmación de eliminación
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [prescriptionToDelete, setPrescriptionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    doctorName: '',
    condition: '',
    symptoms: '',
    dateFrom: '',
    dateTo: '',
    status: 'All'
  });

  // **FUNCIÓN PARA TRANSFORMAR DATOS DE API - Igual que PrescriptionTable**
  const transformApiData = async (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];
    
    console.log('Original prescriptions:', apiData.length);
    
    // Obtener IDs únicos de clínicas
    const clinicIds = [...new Set(
      apiData
        .map(p => typeof p.clinic === 'string' ? p.clinic : p.clinic?._id)
        .filter(Boolean)
    )];
    
    // Fetch todos los datos de clínicas en paralelo
    const clinicsData = {};
    if (clinicIds.length > 0) {
      const clinicPromises = clinicIds.map(async (id) => {
        const clinic = await fetchClinicById(id);
        return { id, clinic };
      });
      
      const results = await Promise.all(clinicPromises);
      results.forEach(({ id, clinic }) => {
        if (clinic) {
          clinicsData[id] = clinic;
        }
      });
    }
    
    const transformedData = apiData.map(prescription => {
      // Obtener datos de la clínica
      const clinicId = typeof prescription.clinic === 'string' ? prescription.clinic : prescription.clinic?._id;
      const clinicData = clinicsData[clinicId];
      
      if (!prescription.medicines || !Array.isArray(prescription.medicines) || prescription.medicines.length === 0) {
        console.warn('Prescription without medicines:', prescription._id);
        return [{
          id: prescription._id,
          doctorId: prescription.doctor?._id || '',
          doctorName: prescription.doctor?.fullName || prescription.doctor?.name || 'N/A',
          patientId: prescription.patient?._id || '',
          patientName: prescription.patient?.fullName || prescription.patient?.name || 'N/A',
          clinicId: clinicId || '',
          clinicName: clinicData?.name || 'N/A',
          conditionId: prescription.condition?._id || '',
          conditionName: prescription.condition?.name || 'N/A',
          medicineId: '',
          medicineName: 'N/A',
          dosage: '',
          frequency: '',
          duration: '',
          purpose: '',
          refills: "0",
          instructions: '',
          datePrescribed: prescription.datePrescribed || new Date().toISOString().split('T')[0],
          symptoms: prescription.symptoms || '',
          suggestion: prescription.suggestion || '',
          diseases: prescription.diseases?.map(d => d.name || 'N/A').join(", ") || '',
          diseasesIds: prescription.diseases?.map(d => d._id).join(", ") || '',
          labtests: prescription.labtests?.map(t => t.name || 'N/A').join(", ") || '',
          labtestsIds: prescription.labtests?.map(t => t._id).join(", ") || '',
          isActive: prescription.isActive !== undefined ? prescription.isActive : true
        }];
      }
      
      return prescription.medicines.map((medicine, index) => ({
        id: prescription._id,
        doctorId: prescription.doctor?._id || '',
        doctorName: prescription.doctor?.fullName || prescription.doctor?.name || 'N/A',
        patientId: prescription.patient?._id || '',
        patientName: prescription.patient?.fullName || prescription.patient?.name || 'N/A',
        clinicId: clinicId || '',
        clinicName: clinicData?.name || 'N/A',
        conditionId: prescription.condition?._id || '',
        conditionName: prescription.condition?.name || 'N/A',
        medicineId: medicine._id || medicine.medicineRef || '',
        medicineName: medicine.name || 'N/A',
        dosage: medicine.dosage || '',
        frequency: medicine.frequency || '',
        duration: medicine.duration || '',
        purpose: medicine.purpose || '',
        refills: "0",
        instructions: medicine.purpose || '',
        datePrescribed: prescription.datePrescribed || new Date().toISOString().split('T')[0],
        symptoms: prescription.symptoms || '',
        suggestion: prescription.suggestion || '',
        diseases: prescription.diseases?.map(d => d.name || 'N/A').join(", ") || '',
        diseasesIds: prescription.diseases?.map(d => d._id).join(", ") || '',
        labtests: prescription.labtests?.map(t => t.name || 'N/A').join(", ") || '',
        labtestsIds: prescription.labtests?.map(t => t._id).join(", ") || '',
        isActive: prescription.isActive !== undefined ? prescription.isActive : true,
        originalMedicineIndex: index
      }));
    }).flat();
    
    console.log('Transformed prescriptions:', transformedData.length);
    return transformedData;
  };

  const fetchClinicById = async (clinicId) => {
    try {
      const response = await fetch(`${API_BASE_HEALTH_CENTER_URL}${API_HEALTH_CENTER_BY_ID}/${clinicId}`);
      if (!response.ok) throw new Error('Error al obtener clínica');
      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Error fetching clinic:', err);
      return null;
    }
  };

  // **FUNCIÓN PRINCIPAL PARA CARGAR DATOS - Integrada con transformApiData**
const loadPrescriptions = useCallback(async () => {
  if (!patientId) {
    console.log('No patientId provided, component will show empty state');
    setPrescriptions([]);
    setOriginalPrescriptions([]);
    setFilteredPrescriptions([]);
    onCountUpdate && onCountUpdate(componentName, 0);
    return;
  }
  
  setIsLoading(true);
  try {
    console.log('Loading prescriptions for patient:', patientId);
    
    // OPCIÓN 1: Si el servicio ya tiene método específico para paciente
    const response = await PrescriptionService.getPrescriptionsByPatient(patientId);
    console.log('Raw API response:', response);
    
    let prescriptionsData = [];
    
    if (response.data) {
      prescriptionsData = Array.isArray(response.data) ? response.data : [response.data];
    } else if (Array.isArray(response)) {
      prescriptionsData = response;
    }

    // FILTRAR EXPLÍCITAMENTE POR PATIENT ID (por si el servicio no filtra correctamente)
    prescriptionsData = prescriptionsData.filter(prescription => {
      const prescriptionPatientId = prescription.patient?._id || prescription.patient;
      return prescriptionPatientId === patientId;
    });

    console.log('Filtered prescriptions for patient:', prescriptionsData.length);
    console.log('Prescriptions data for transformation:', prescriptionsData);

    // Guardar datos originales
    setOriginalPrescriptions(prescriptionsData);
    
    // Transformar datos usando la misma lógica de PrescriptionTable
    const transformedData = await transformApiData(prescriptionsData);
setPrescriptions(transformedData);
setFilteredPrescriptions(transformedData);

// Update counter with the new data length, not the old one
onCountUpdate && onCountUpdate(componentName, transformedData.length);
    
    // Actualizar contador
    if (prescriptions.length !== transformedData.length) {
      console.log(`📊 ${componentName}: Contador cambió de ${prescriptions.length} a ${transformedData.length}`);
      onCountUpdate && onCountUpdate(componentName, transformedData.length);
    }
    
  } catch (error) {
    console.error('Error loading prescriptions:', error);
    
    // OPCIÓN 2: Fallback - Si el servicio específico falla, usar el servicio general y filtrar
    try {
      console.log('Fallback: Loading all prescriptions and filtering by patient');
      const allPrescriptionsResponse = await prescriptionService.getAllPrescriptions();
      
      let allPrescriptionsData = [];
      if (allPrescriptionsResponse.data) {
        allPrescriptionsData = Array.isArray(allPrescriptionsResponse.data) 
          ? allPrescriptionsResponse.data 
          : [allPrescriptionsResponse.data];
      }
      
      // Filtrar por ID de paciente
      const patientPrescriptions = allPrescriptionsData.filter(prescription => {
        const prescriptionPatientId = prescription.patient?._id || prescription.patient;
        return prescriptionPatientId === patientId;
      });
      
      console.log('Fallback filtered prescriptions:', patientPrescriptions.length);
      
      // Guardar datos originales
      setOriginalPrescriptions(patientPrescriptions);
      
      // Transformar datos
      const transformedData = await transformApiData(patientPrescriptions);
      setPrescriptions(transformedData);
      setFilteredPrescriptions(transformedData);
      
      // Actualizar contador
      onCountUpdate && onCountUpdate(componentName, transformedData.length);
      
    } catch (fallbackError) {
      console.error('Fallback error:', fallbackError);
      setPrescriptions([]);
      setOriginalPrescriptions([]);
      setFilteredPrescriptions([]);
      onCountUpdate && onCountUpdate(componentName, 0);
    }
  } finally {
    setIsLoading(false);
  }
}, [patientId, prescriptions.length, onCountUpdate, componentName]);

// **FUNCIÓN ADICIONAL PARA DEBUG - Verificar datos del paciente**
const debugPatientData = () => {
  console.log('=== PATIENT DEBUG INFO ===');
  console.log('Patient Data from location.state:', patientData);
  console.log('Patient ID:', patientId);
  console.log('Original Prescriptions:', originalPrescriptions.length);
  console.log('Transformed Prescriptions:', prescriptions.length);
  console.log('Filtered Prescriptions:', filteredPrescriptions.length);
  
  // Verificar IDs de paciente en las prescripciones
  prescriptions.forEach((prescription, index) => {
    console.log(`Prescription ${index}:`, {
      id: prescription.id,
      patientId: prescription.patientId,
      patientName: prescription.patientName,
      matchesCurrentPatient: prescription.patientId === patientId
    });
  });
};

// **COMPONENTE DE DEBUG (opcional, para development)**
const DebugInfo = () => {
  if (process.env.NODE_ENV !== 'development') return null;
  
  return (
    <div className="alert alert-info mt-3">
      <h6>Debug Information:</h6>
      <p><strong>Patient ID:</strong> {patientId}</p>
      <p><strong>Patient Name:</strong> {patientData?.fullName || patientData?.name || 'N/A'}</p>
      <p><strong>Original Prescriptions:</strong> {originalPrescriptions.length}</p>
      <p><strong>Transformed Prescriptions:</strong> {prescriptions.length}</p>
      <p><strong>Filtered Prescriptions:</strong> {filteredPrescriptions.length}</p>
      <button 
        className="btn btn-sm btn-outline-info" 
        onClick={debugPatientData}
      >
        Log Debug Info
      </button>
    </div>
  );
};

  // **FUNCIÓN PARA MANEJAR CAMBIO DE STATUS - Igual que PrescriptionTable**
  const handleStatusChange = async (prescriptionId, newStatus) => {
    try {
      console.log('Attempting to update prescription:', prescriptionId, 'to status:', newStatus);
      
      // Call the service method to update the status
      const result = await prescriptionService.updatePrescriptionActiveStatus(prescriptionId, newStatus);
      console.log('API response:', result);
      
      // Update local state immediately without page reload
      setPrescriptions(prevPrescriptions => 
        prevPrescriptions.map(prescription => 
          prescription.id === prescriptionId 
            ? { ...prescription, isActive: newStatus }
            : prescription
        )
      );

      // Update original prescriptions state
      setOriginalPrescriptions(prevOriginal => 
        prevOriginal.map(prescription => 
          prescription._id === prescriptionId 
            ? { ...prescription, isActive: newStatus }
            : prescription
        )
      );

      // Update filtered prescriptions state
      setFilteredPrescriptions(prevFiltered => 
        prevFiltered.map(prescription => 
          prescription.id === prescriptionId 
            ? { ...prescription, isActive: newStatus }
            : prescription
        )
      );
      
      console.log(`Prescription ${prescriptionId} status updated to ${newStatus ? 'active' : 'inactive'}`);
      
    } catch (error) {
      console.error(`Error updating prescription status:`, error);
      console.error('Error details:', error.response?.data || error.message);
      setAlertInfo({
        type: 'danger',
        message: 'Error updating prescription status. Please try again.'
      });
    }
  };

  // **FUNCIÓN PARA ELIMINAR PRESCRIPCIONES - Implementada**
  const handleDeletePrescription = (prescriptionId) => {
    const prescription = prescriptions.find(p => p.id === prescriptionId);
    if (prescription) {
      setPrescriptionToDelete(prescription);
      setShowDeleteConfirm(true);
    }
  };

const confirmDeletePrescription = async () => {
  if (!prescriptionToDelete) return;
  
  setIsDeleting(true);
  
  try {
    console.log('Deleting prescription:', prescriptionToDelete.id);
    
    // Call the service method to delete the prescription
    const result = await prescriptionService.deletePrescription(prescriptionToDelete.id);
    console.log('Delete API response:', result);
    
    // Check if deletion was successful - be more flexible with success checking
    if (result && result.status === false) {
      throw new Error(result.message || 'Failed to delete prescription');
    }
    
    // If we get here, assume deletion was successful
    const prescriptionId = prescriptionToDelete.id;
    
    // Update all state arrays
    setPrescriptions(prevPrescriptions => {
      const filtered = prevPrescriptions.filter(prescription => prescription.id !== prescriptionId);
      console.log('Updated prescriptions count:', filtered.length);
      return filtered;
    });

    setOriginalPrescriptions(prevOriginal => {
      const filtered = prevOriginal.filter(prescription => prescription._id !== prescriptionId);
      console.log('Updated original prescriptions count:', filtered.length);
      return filtered;
    });

    setFilteredPrescriptions(prevFiltered => {
      const filtered = prevFiltered.filter(prescription => prescription.id !== prescriptionId);
      console.log('Updated filtered prescriptions count:', filtered.length);
      return filtered;
    });
    
    // Update counter - calculate new count first
    const newCount = prescriptions.filter(p => p.id !== prescriptionId).length;
    console.log('Updating counter to:', newCount);
    
    // Update counter with error handling
    try {
      onCountUpdate && onCountUpdate(componentName, newCount);
    } catch (counterError) {
      console.warn('Error updating counter:', counterError);
      // Don't throw, just log the warning
    }
    
    // Reset selection
    setRowSelection({});
    
    // Show success message
    setAlertInfo({
      type: 'success',
      message: `Prescription deleted successfully.`
    });
    
    console.log(`Prescription ${prescriptionId} deleted successfully`);
    
    // Auto-hide success message after 3 seconds
    setTimeout(() => {
      setAlertInfo(null);
    }, 3000);
    
  } catch (error) {
    console.error('Error deleting prescription:', error);
    
    // Determine error message
    let errorMessage = 'Error deleting prescription. Please try again.';
    
    if (error.message && error.message !== 'Error desconocido') {
      errorMessage = error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.statusText) {
      errorMessage = `Server error: ${error.response.statusText}`;
    }
    
    setAlertInfo({
      type: 'danger',
      message: errorMessage
    });
    
    // Auto-hide error message after 5 seconds
    setTimeout(() => {
      setAlertInfo(null);
    }, 5000);
    
  } finally {
    // Always reset these states, even if there's an error
    setIsDeleting(false);
    setShowDeleteConfirm(false);
    setPrescriptionToDelete(null);
  }
};

  const cancelDeletePrescription = () => {
    setShowDeleteConfirm(false);
    setPrescriptionToDelete(null);
  };

  // **FUNCIÓN PARA ELIMINAR PRESCRIPCIONES - Integrada**
  const refreshData = (removedItem = null) => {
    if (removedItem) {
      const updatedPrescriptions = prescriptions.filter(p => p.id !== removedItem.id);
      setPrescriptions(updatedPrescriptions);
      
      // Also update original prescriptions
      const updatedOriginal = originalPrescriptions.filter(p => p._id !== removedItem.id);
      setOriginalPrescriptions(updatedOriginal);
      
      // Update filtered prescriptions
      const updatedFiltered = filteredPrescriptions.filter(p => p.id !== removedItem.id);
      setFilteredPrescriptions(updatedFiltered);
      
      // Reset selection after deletion
      setRowSelection({});
      
      // Update counter
      onCountUpdate && onCountUpdate(componentName, updatedPrescriptions.length);
    }
  };

  // **FUNCIÓN PARA HIGHLIGHTING TEXT - Igual que PrescriptionTable**
  const highlightText = (text, filterTerm, fieldName) => {
    // Step 1: Verify if the text is undefined or null
    if (text === undefined || text === null) {
      return '';
    }
    
    // Step 2: Convert text to string if it's not
    const textStr = typeof text === 'string' ? text : String(text);
    
    // Step 3: Get the filter term with validations
    let termToHighlight = '';
    if (fieldName && activeFilters && activeFilters[fieldName]) {
      termToHighlight = activeFilters[fieldName].toLowerCase();
    } else if (globalFilter) {
      termToHighlight = globalFilter.toLowerCase();
    }
    
    // Step 4: Check if there's a filter term or global filter
    const hasGlobalFilter = globalFilter !== undefined && globalFilter !== null && globalFilter !== '';
    if (!termToHighlight && !hasGlobalFilter) {
      return textStr;
    }
    
    // Step 5: Use the global filter if there's no specific term
    let term = termToHighlight;
    if (!term && hasGlobalFilter) {
      term = globalFilter.toLowerCase();
    }
    
    // Step 6: Final check before splitting
    if (!term || !textStr || !textStr.toLowerCase().includes(term)) {
      return textStr;
    }
    
    // Step 7: Split the text and highlight matches
    try {
      const parts = textStr.split(new RegExp(`(${term})`, 'gi'));
      
      return (
        <>
          {parts.map((part, index) => {
            if (!part) return null;
            return part.toLowerCase() === term.toLowerCase() ? 
              <span key={index} style={{ backgroundColor: '#FFFF00', fontWeight: 'bold' }}>{part}</span> : 
              part;
          })}
        </>
      );
    } catch (error) {
      console.error("Error highlighting text:", error);
      return textStr; // Return original text in case of error
    }
  };

  // **FUNCIONES PARA EDICIÓN INLINE - Igual que PrescriptionTable**
  const handleCellDoubleClick = (cell, row) => {
    if (isEditableField(cell.column.id)) {
      if (editingCell === null) {
        setEditingCell({
          rowId: row.index,
          columnId: cell.column.id,
          value: cell.getValue() || ''
        });
        setEditValue(cell.getValue() || '');
      }
    }
  };

  const isEditableField = (columnId) => {
    const editableFields = [
      'dosage', 
      'frequency', 
      'duration', 
      'instructions',
      'symptoms',
      'suggestion',
      'purpose'
    ];
    
    return editableFields.includes(columnId);
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleSaveEdit = async () => {
    try {
      if (!editingCell) return;
      
      // Get the current row data
      const row = prescriptions[editingCell.rowId];
      if (!row) return;
      
      // Find the original prescription in originalPrescriptions
      const prescriptionId = row.id;
      const originalPrescription = originalPrescriptions.find(p => p._id === prescriptionId);
      
      if (!originalPrescription) {
        console.error('Original prescription not found');
        return;
      }
      
      // Prepare data for API update based on which field is being edited
      let updateData = {};
      
      if (editingCell.columnId === 'symptoms') {
        updateData = {
          symptoms: editValue
        };
      } else if (editingCell.columnId === 'suggestion') {
        updateData = {
          suggestion: editValue
        };
      } else {
        // For medicine-related fields, we need to update the medicine array
        const medicineIndex = row.originalMedicineIndex;
        
        // Create a copy of the prescription's medicine array
        const updatedMedicines = [...originalPrescription.medicines];
        
        // Update the specific field in the specific medicine object
        if (medicineIndex >= 0 && medicineIndex < updatedMedicines.length) {
          updatedMedicines[medicineIndex] = {
            ...updatedMedicines[medicineIndex],
            [editingCell.columnId]: editValue
          };
          
          updateData = {
            medicines: updatedMedicines
          };
        }
      }
      
      // Call API to update prescription
      await prescriptionService.updatePrescription(prescriptionId, updateData);
      
      // Update local state
      setPrescriptions(prevPrescriptions => {
        const newPrescriptions = [...prevPrescriptions];
        newPrescriptions[editingCell.rowId] = {
          ...newPrescriptions[editingCell.rowId],
          [editingCell.columnId]: editValue
        };
        return newPrescriptions;
      });
      
      // Update filtered prescriptions
      setFilteredPrescriptions(prevFiltered => {
        const newFiltered = [...prevFiltered];
        const filteredIndex = newFiltered.findIndex(p => p.id === prescriptionId);
        if (filteredIndex >= 0) {
          newFiltered[filteredIndex] = {
            ...newFiltered[filteredIndex],
            [editingCell.columnId]: editValue
          };
        }
        return newFiltered;
      });
      
      // Reset editing state
      setEditingCell(null);
      setEditValue('');
      
      console.log(`Updated prescription ${prescriptionId}, field: ${editingCell.columnId}, value: ${editValue}`);
      
    } catch (error) {
      console.error('Error updating prescription:', error);
      setAlertInfo({
        type: 'danger',
        message: 'Error updating prescription. Please try again.'
      });
    }
  };

  // Custom cell renderer that handles edit mode
  const renderCell = (cell, row) => {
    const isEditing = 
      editingCell && 
      editingCell.rowId === row.index && 
      editingCell.columnId === cell.column.id;
    
    if (isEditing) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            variant="standard"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            size="small"
            autoFocus
            fullWidth
            onClick={(e) => e.stopPropagation()} // Prevent row selection
          />
          <IconButton 
            size="small" 
            color="primary" 
            onClick={(e) => {
              e.stopPropagation();
              handleSaveEdit();
            }}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            color="secondary" 
            onClick={(e) => {
              e.stopPropagation();
              handleCancelEdit();
            }}
          >
            <CancelIcon fontSize="small" />
          </IconButton>
        </div>
      );
    }
    
    // Return the normal cell content with highlighting when not editing
    return highlightText(cell.getValue(), 
      cell.column.id in activeFilters ? activeFilters[cell.column.id] : null, 
      cell.column.id);
  };

  // **DEFINICIÓN DE COLUMNAS - Adaptada de PrescriptionTable CON EDIT Y DELETE**
  const columns = useMemo(
  () => [    
    {
      accessorKey: 'id',
      header: 'Prescription ID',
      size: 150,
      Cell: ({ cell, row }) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{renderCell(cell, row)}</div>
          {/* Mostrar confirmación de que es del paciente correcto */}
          {row.original.patientId === patientId && (
            <div style={{ fontSize: '0.7em', color: '#28a745' }}>✓ Current Patient</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'doctorName',
      header: 'Doctor',
      size: 180,
      Cell: ({ cell, row }) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{renderCell(cell, row)}</div>
          <div style={{ fontSize: '0.8em', color: '#666' }}>ID: {row.original.doctorId}</div>
        </div>
      ),
    },
    // ... resto de las columnas igual
    {
      accessorKey: 'clinicName',
      header: 'Clinic',
      size: 150,
      Cell: ({ cell, row }) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{renderCell(cell, row)}</div>
          <div style={{ fontSize: '0.8em', color: '#666' }}>ID: {row.original.clinicId}</div>
        </div>
      ),
    },
    {
      accessorKey: 'conditionName',
      header: 'Condition',
      size: 150,
      Cell: ({ cell, row }) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{renderCell(cell, row)}</div>
          <div style={{ fontSize: '0.8em', color: '#666' }}>ID: {row.original.conditionId}</div>
        </div>
      ),
    },
    {
      accessorKey: 'symptoms',
      header: 'Symptoms',
      size: 200,
      Cell: ({ cell, row }) => renderCell(cell, row),
    },
    {
      accessorKey: 'suggestion',
      header: 'Suggestion',
      size: 200,
      Cell: ({ cell, row }) => renderCell(cell, row),
    },
    {
      accessorKey: 'medicineName',
      header: 'Medicine',
      size: 180,
      Cell: ({ cell, row }) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{renderCell(cell, row)}</div>
          <div style={{ fontSize: '0.8em', color: '#666' }}>ID: {row.original.medicineId}</div>
        </div>
      ),
    },
    {
      accessorKey: 'dosage',
      header: 'Dosage',
      size: 120,
      Cell: ({ cell, row }) => renderCell(cell, row),
    },
    {
      accessorKey: 'frequency',
      header: 'Frequency',
      size: 150,
      Cell: ({ cell, row }) => renderCell(cell, row),
    },
    {
      accessorKey: 'duration',
      header: 'Duration',
      size: 120,
      Cell: ({ cell, row }) => renderCell(cell, row),
    },
    {
      accessorKey: 'purpose',
      header: 'Purpose',
      size: 150,
      Cell: ({ cell, row }) => renderCell(cell, row),
    },
    {
      accessorKey: 'diseases',
      header: 'Diseases',
      size: 200,
      Cell: ({ cell, row }) => renderCell(cell, row),
    },
    {
      accessorKey: 'labtests',
      header: 'Lab Tests',
      size: 200,
      Cell: ({ cell, row }) => renderCell(cell, row),
    },
    {
      accessorKey: 'datePrescribed',
      header: 'Date',
      size: 150,
      Cell: ({ cell, row }) => renderCell(cell, row),
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 180,
      enableColumnOrdering: false,
      Cell: ({ row }) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleViewPrescription(row.original.id);
            }}
            title="View Details"
          >
            <FiEye />
          </button>
          <button
            className="btn btn-outline-warning btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleEditPrescription(row.original.id);
            }}
            title="Edit Prescription"
          >
            <FiEdit3 />
          </button>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDeletePrescription(row.original.id);
            }}
            title="Delete Prescription"
          >
            <FiTrash2 />
          </button>
        </div>          
      ),
    },
  ],
  [activeFilters, globalFilter, editingCell, editValue, handleStatusChange, refreshData, renderCell, patientId]
);

  // **FUNCIÓN PARA APLICAR FILTROS**
  const applyFilters = useCallback(() => {
    console.log('🔄 Aplicando filtros y ordenamiento...');
    console.log('Prescriptions array length:', prescriptions.length);
    
    let filtered = [...prescriptions];

    // Aplicar filtro de búsqueda global
    if (globalFilter) {
      filtered = filtered.filter(prescription => 
        Object.entries(prescription).some(([key, value]) => {
          return typeof value === 'string' && value.toLowerCase().includes(globalFilter.toLowerCase());
        })
      );
    }

    // Aplicar filtros específicos
    if (filters.status !== 'All') {
      if (filters.status === 'Active') {
        filtered = filtered.filter(prescription => prescription.isActive);
      } else if (filters.status === 'Inactive') {
        filtered = filtered.filter(prescription => !prescription.isActive);
      }
    }

    if (filters.doctorName) {
      filtered = filtered.filter(prescription => 
        (prescription.doctorName || '').toLowerCase().includes(filters.doctorName.toLowerCase())
      );
    }

    if (filters.condition) {
      filtered = filtered.filter(prescription => 
        (prescription.conditionName || '').toLowerCase().includes(filters.condition.toLowerCase())
      );
    }

    if (filters.symptoms) {
      filtered = filtered.filter(prescription => 
        (prescription.symptoms || '').toLowerCase().includes(filters.symptoms.toLowerCase())
      );
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(prescription => {
        const createdDate = prescription.datePrescribed;
        return createdDate && new Date(createdDate) >= new Date(filters.dateFrom);
      });
    }

    if (filters.dateTo) {
      filtered = filtered.filter(prescription => {
        const createdDate = prescription.datePrescribed;
        return createdDate && new Date(createdDate) <= new Date(filters.dateTo);
      });
    }

    // Ordenamiento
    filtered.sort((a, b) => {
      const getDate = (item) => {
        const dateValue = item.datePrescribed || new Date().toISOString();
        return new Date(dateValue);
      };
      
      switch (sortBy) {
        case 'newest':
          return getDate(b) - getDate(a);
        case 'oldest':
          return getDate(a) - getDate(b);
        case 'doctor':
          const doctorA = (a.doctorName || '').toLowerCase();
          const doctorB = (b.doctorName || '').toLowerCase();
          return doctorA.localeCompare(doctorB);
        case 'condition':
          const conditionA = (a.conditionName || '').toLowerCase();
          const conditionB = (b.conditionName || '').toLowerCase();
          return conditionA.localeCompare(conditionB);
        default:
          return 0;
      }
    });

    console.log('Final filtered array length:', filtered.length);
    setFilteredPrescriptions(filtered);
  }, [prescriptions, filters, globalFilter, sortBy]);

  // **FUNCIONES DE HANDLERS**
  const handleGlobalFilterChange = (filter) => {
    setGlobalFilter(filter);
  };

  const handleViewPrescription = useCallback((prescriptionId) => {
    console.log('Viewing prescription:', prescriptionId);
    const prescription = prescriptions.find(p => p.id === prescriptionId);
    if (prescription) {
      console.log('Found prescription for viewing:', prescription);
      setSelectedPrescription(prescription);
      setShowViewOffcanvas(true);
    } else {
      console.error('Prescription not found:', prescriptionId);
    }
  }, [prescriptions]);

// **FUNCIONES PARA SORTING**
  const handleSortChange = useCallback((sortOption) => {
    console.log('Cambiando orden a:', sortOption);
    setSortBy(sortOption);
    setShowSortDropdown(false);
  }, []);

  const getSortDisplayText = () => {
    switch (sortBy) {
      case 'newest': return 'Newest First';
      case 'oldest': return 'Oldest First';
      case 'doctor': return 'By Doctor';
      case 'condition': return 'By Condition';
      default: return 'Sort by';
    }
  };

  // **FUNCIONES PARA MANEJAR OFFCANVAS**
  const handleOpenFilterOffcanvas = () => {
    // Cerrar cualquier offcanvas abierto antes de abrir el de filtros
    setShowAddOffcanvas(false);
    setShowViewOffcanvas(false);
    setShowEditOffcanvas(false);
    setShowFilterOffcanvas(true);
  };

  const handleCloseFilterOffcanvas = () => {
    setShowFilterOffcanvas(false);
  };

  const handleCloseViewOffcanvas = () => {
    console.log('Closing view offcanvas');
    setShowViewOffcanvas(false);
    setSelectedPrescription(null);
  };

  const handleAddNew = () => {
    console.log('Add New button clicked - opening modal');
    // Cerrar cualquier offcanvas abierto antes de abrir el de agregar
    setShowFilterOffcanvas(false);
    setShowViewOffcanvas(false);
    setShowEditOffcanvas(false);
    setShowAddOffcanvas(true);
  };

  // **FUNCIONES PARA FILTROS ACTIVOS**
  const getActiveFilters = () => {
    const activeFilters = [];
    
    if (globalFilter) {
      activeFilters.push({
        key: 'search',
        label: `Search: "${globalFilter}"`,
        value: globalFilter,
        type: 'search'
      });
    }
    
    if (filters.status !== 'All') {
      activeFilters.push({
        key: 'status',
        label: `Status: ${filters.status}`,
        value: filters.status,
        type: 'status'
      });
    }
    
    if (filters.doctorName) {
      activeFilters.push({
        key: 'doctorName',
        label: `Doctor: ${filters.doctorName}`,
        value: filters.doctorName,
        type: 'doctorName'
      });
    }
    
    if (filters.condition) {
      activeFilters.push({
        key: 'condition',
        label: `Condition: ${filters.condition}`,
        value: filters.condition,
        type: 'condition'
      });
    }
    
    if (filters.symptoms) {
      activeFilters.push({
        key: 'symptoms',
        label: `Symptoms: ${filters.symptoms}`,
        value: filters.symptoms,
        type: 'symptoms'
      });
    }
    
    if (filters.dateFrom) {
      activeFilters.push({
        key: 'dateFrom',
        label: `From: ${filters.dateFrom}`,
        value: filters.dateFrom,
        type: 'dateFrom'
      });
    }
    
    if (filters.dateTo) {
      activeFilters.push({
        key: 'dateTo',
        label: `To: ${filters.dateTo}`,
        value: filters.dateTo,
        type: 'dateTo'
      });
    }
    
    if (sortBy !== 'newest') {
      activeFilters.push({
        key: 'sort',
        label: `Sort: ${getSortDisplayText()}`,
        value: sortBy,
        type: 'sort'
      });
    }
    
    return activeFilters;
  };

  const handleRemoveFilter = (filterToRemove) => {
    switch (filterToRemove.type) {
      case 'search':
        setGlobalFilter('');
        break;
      case 'status':
        setFilters(prev => ({ ...prev, status: 'All' }));
        break;
      case 'doctorName':
        setFilters(prev => ({ ...prev, doctorName: '' }));
        break;
      case 'condition':
        setFilters(prev => ({ ...prev, condition: '' }));
        break;
      case 'symptoms':
        setFilters(prev => ({ ...prev, symptoms: '' }));
        break;
      case 'dateFrom':
        setFilters(prev => ({ ...prev, dateFrom: '' }));
        break;
      case 'dateTo':
        setFilters(prev => ({ ...prev, dateTo: '' }));
        break;
      case 'sort':
        setSortBy('newest');
        break;
      default:
        break;
    }
  };

  const handleClearAllActiveFilters = () => {
    setGlobalFilter('');
    setFilters({
      doctorName: '',
      condition: '',
      symptoms: '',
      dateFrom: '',
      dateTo: '',
      status: 'All'
    });
    setSortBy('newest');
  };


  // **EFECTOS**
  // Remove prescriptions.length from dependencies to prevent infinite loops
useEffect(() => {
  console.log(`🔄 ${componentName}: useEffect triggered, patientId:`, patientId);
  if (patientId) {
    loadPrescriptions();
  }
}, [patientId, loadPrescriptions, componentName]); // Remove prescriptions.length

  useEffect(() => {
    console.log('📊 useEffect applyFilters triggered');
    console.log('- prescriptions.length:', prescriptions.length);
    console.log('- sortBy:', sortBy);
    console.log('- filters:', filters);
    console.log('- globalFilter:', globalFilter);
    
    if (prescriptions.length > 0) {
      applyFilters();
    }
  }, [prescriptions, filters, globalFilter, sortBy]);

// Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Prevent body scroll when offcanvas are open
  useEffect(() => {
    if (showFilterOffcanvas || showAddOffcanvas || showViewOffcanvas || showEditOffcanvas) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '17px';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    };
  }, [showFilterOffcanvas, showAddOffcanvas, showViewOffcanvas, showEditOffcanvas]);

  
  // **FUNCIONES PARA OFFCANVAS Y FORMULARIOS**
  const handleAddPrescription = () => {
    console.log('Opening add prescription form for patient:', patientId);
    setShowAddOffcanvas(true);
  };

  const handleEditPrescription = (prescriptionId) => {
    console.log('Editing prescription:', prescriptionId);
    const prescription = originalPrescriptions.find(p => p._id === prescriptionId);
    if (prescription) {
      setEditingPrescription(prescription);
      setShowEditOffcanvas(true);
    }
  };

  const handleCloseOffcanvas = () => {
    setShowFilterOffcanvas(false);
    setShowViewOffcanvas(false);
    setShowEditOffcanvas(false);
    setShowAddOffcanvas(false);
    setSelectedPrescription(null);
    setEditingPrescription(null);
  };

  const handleFormSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      if (editingPrescription) {
        // Actualizar prescripción existente
        await prescriptionService.updatePrescription(editingPrescription._id, formData);
      } else {
        // Crear nueva prescripción
        const prescriptionData = {
          ...formData,
          patient: patientId
        };
        await prescriptionService.createPrescription(prescriptionData);
      }
      
      // Recargar datos
      await loadPrescriptions();
      handleCloseOffcanvas();
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Error saving prescription. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFilterApply = () => {
    console.log('Applying filters:', filters);
    setActiveFilters({ ...filters });
    setShowFilterOffcanvas(false);
    applyFilters();
  };

  const handleFilterReset = () => {
    setFilters({
      doctorName: '',
      condition: '',
      symptoms: '',
      dateFrom: '',
      dateTo: '',
      status: 'All'
    });
    setActiveFilters({});
    setGlobalFilter('');
  };

  // **RENDER DEL COMPONENTE**
  if (!patientId) {
    return (
      <div className="container-fluid">
        <TopHeader />
        <PatientManagement />
        <div className="alert alert-warning" role="alert">
          <FiAlertTriangle className="me-2" />
          No patient selected. Please select a patient to view their prescriptions.
        </div>
      </div>
    );
  }



  
  return (
    <main id="app">
    
      <TopHeader />
      <div className="container-fluid w-100">
      <PatientManagement activeMenu='/my-prescriptions' />
      
      {/* Breadcrumb */}
        <div className="mb-3">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/dashboard">Dashboard</a></li>
              <li className="breadcrumb-item"><a href="/patients/list">Patients</a></li>
              <li className="breadcrumb-item active" aria-current="page">
  My Prescriptions {patientData?.patientName ? `- ${patientData.patientName}` : ''}
</li>
            </ol>
          </nav>
        </div>

      {/* <DebugInfo /> */}

      
<div className="card-body">
      {/* Header con título y controles */}
      <div className="row mb-3">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between">
                
            {/* Left side - Search */}
                            <div className="d-flex align-items-center gap-2">
                              <div className="input-group" style={{maxWidth: '300px'}}>
                                <span className="input-group-text">
                                  <FiSearch />
                                </span>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search"
                                  value={globalFilter}
                                  onChange={(e) => handleGlobalFilterChange(e.target.value)}
                                />
                              </div>
                            </div>
              
              <div className="d-flex align-items-center gap-2">
                                {/* Sort By Dropdown */}
                                <div className="dropdown" style={{position: 'relative'}}>
                                  <button
                                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowSortDropdown(!showSortDropdown);
                                    }}
                                  >
                                    {getSortDisplayText()}
                                    <FiChevronDown />
                                  </button>
                                  {showSortDropdown && (
                                    <div className="dropdown-menu show" style={{position: 'absolute', top: '100%', right: 0, zIndex: 1000}}>
                                      <button
                                        className={`dropdown-item ${sortBy === 'newest' ? 'active' : ''}`}
                                        onClick={() => handleSortChange('newest')}
                                      >
                                        Newest First
                                      </button>
                                      <button
                                        className={`dropdown-item ${sortBy === 'oldest' ? 'active' : ''}`}
                                        onClick={() => handleSortChange('oldest')}
                                      >
                                        Oldest First
                                      </button>
                                      <button
                                        className={`dropdown-item ${sortBy === 'severity' ? 'active' : ''}`}
                                        onClick={() => handleSortChange('severity')}
                                      >
                                        By Severity
                                      </button>
                                      <button
                                        className={`dropdown-item ${sortBy === 'alphabetical' ? 'active' : ''}`}
                                        onClick={() => handleSortChange('alphabetical')}
                                      >
                                        A-Z
                                      </button>
                                    </div>
                                  )}
                                </div>
              
                                {/* Filter Button */}
                                <button
                                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                                  onClick={handleOpenFilterOffcanvas}
                                >
                                  <FiFilter />
                                  Filter
                                </button>
                          
                                {/* Add New Button */}
                                <button
                                  className="btn btn-primary d-flex align-items-center gap-2"
                                  onClick={handleAddNew}
                                >
                                  <FiPlus />
                                  Add New
                                </button>
                              </div>
          </div>
        </div>
      </div>
{/* Active Filters Display */}
          {getActiveFilters().length > 0 && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex align-items-center gap-2 p-3 bg-light rounded">
                  <span className="text-muted fw-medium">Active filters:</span>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {getActiveFilters().map((filter) => (
                      <span
                        key={filter.key}
                        className="badge bg-primary d-flex align-items-center gap-1"
                        style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}
                      >
                        {filter.label}
                        <button
                          type="button"
                          className="btn-close btn-close-white"
                          style={{ 
                            fontSize: '0.625rem',
                            padding: '0.125rem',
                            marginLeft: '0.25rem'
                          }}
                          onClick={() => handleRemoveFilter(filter)}
                          title={`Remove ${filter.label} filter`}
                        ></button>
                      </span>
                    ))}
                    
                    {/* Clear All Button */}
                    {getActiveFilters().length > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                        onClick={handleClearAllActiveFilters}
                        title="Clear all filters"
                      >
                        <FiX size={14} />
                        Clear All
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
                <MaterialReactTable
                  columns={columns}
                  data={filteredPrescriptions}
                  enableRowSelection
                  onRowSelectionChange={setRowSelection}
                  state={{
                    rowSelection,
                    globalFilter,
                  }}
                  initialState={{
                    pagination: { pageSize: 10 },
                    density: 'compact'
                  }}
                  muiTableProps={{
                    sx: {
                      '& .MuiTableHead-root': {
                        backgroundColor: '#f8f9fa',
                      },
                      '& .MuiTableCell-root': {
                        padding: '8px 12px',
                      },
                      '& .MuiTableRow-root:hover': {
                        backgroundColor: '#f5f5f5',
                        cursor: 'pointer'
                      }
                    }
                  }}
                  muiTableBodyRowProps={({ row }) => ({
                    onClick: () => handleViewPrescription(row.original.id),
                    onDoubleClick: (event) => {
                      // Handle double click for cell editing
                      const cell = event.target.closest('td');
                      if (cell) {
                        const columnId = cell.getAttribute('data-column-id');
                        if (columnId && isEditableField(columnId)) {
                          // Find the cell and trigger edit
                          const cellData = {
                            column: { id: columnId },
                            getValue: () => row.original[columnId]
                          };
                          handleCellDoubleClick(cellData, row);
                        }
                      }
                    }
                  })}
                  enableGlobalFilter // Deshabilitamos el filtro interno
                  enableColumnFilters={false}
                  enableSorting// Usamos nuestro sistema de ordenamiento
                  enablePagination
                  enableBottomToolbar
                />
              
            {/* Results Summary */}
          {filteredPrescriptions.length > 0 && (
            <div className="mt-3">
              <small className="text-muted">
                Showing {filteredPrescriptions.length} of {prescriptions.length} allergies
                {Object.values(filters).some(f => f && f !== 'All') || globalFilter ? ' (filtered)' : ''}
              </small>
            </div>
          )}
        </div>
      </div>
        
      

      {/* Offcanvas para filtros */}
      {showFilterOffcanvas && (
        <>
        <div className="offcanvas-backdrop fade show" onClick={handleCloseFilterOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1">
            <div className="offcanvas-header">
            <h5 className="offcanvas-title">
              Filter Prescriptions
            </h5>
            <button 
              type="button" 
              className="btn-close"
              onClick={handleCloseOffcanvas}
            ></button>
          </div>
          <div className="offcanvas-body">
            <form>
              <div className="mb-3">
                <label className="form-label">Status</label>
                <select 
                  className="form-select"
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                >
                  <option value="All">All</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              
              <div className="mb-3">
                <label className="form-label">Doctor Name</label>
                <input 
                  type="text" 
                  className="form-control"
                  value={filters.doctorName}
                  onChange={(e) => setFilters({...filters, doctorName: e.target.value})}
                  placeholder="Filter by doctor name..."
                />
              </div>
              
              <div className="mb-3">
                <label className="form-label">Condition</label>
                <input 
                  type="text" 
                  className="form-control"
                  value={filters.condition}
                  onChange={(e) => setFilters({...filters, condition: e.target.value})}
                  placeholder="Filter by condition..."
                />
              </div>
              
              <div className="mb-3">
                <label className="form-label">Symptoms</label>
                <input 
                  type="text" 
                  className="form-control"
                  value={filters.symptoms}
                  onChange={(e) => setFilters({...filters, symptoms: e.target.value})}
                  placeholder="Filter by symptoms..."
                />
              </div>
              
              <div className="mb-3">
                <label className="form-label">Date From</label>
                <input 
                  type="date" 
                  className="form-control"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                />
              </div>
              
              <div className="mb-3">
                <label className="form-label">Date To</label>
                <input 
                  type="date" 
                  className="form-control"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                />
              </div>
              
              <div className="d-grid gap-2">
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={handleFilterApply}
                >
                  Apply Filters
                </button>
                <button 
                  type="button" 
                  className="btn btn-outline-secondary"
                  onClick={handleFilterReset}
                >
                  Reset Filters
                </button>
              </div>
            </form>
          </div>
        </div>
        </>
      )}

      {/* Offcanvas para ver prescripción */}
      {showViewOffcanvas && selectedPrescription && (
        <>
        <div className="offcanvas-backdrop fade show" onClick={handleCloseViewOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{width: '600px'}}>
            <div className="offcanvas-header">
            <h5 className="offcanvas-title">
              Prescription Details
            </h5>
            <button 
              type="button" 
              className="btn-close"
              onClick={handleCloseOffcanvas}
            ></button>
          </div>
          <div className="offcanvas-body">
            <div className="row g-3">
                {/* Basic Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">
                      General Information
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-6">
                        <strong>Prescription ID:</strong><br />
                        {selectedPrescription.id}
                      </div>
                      <div className="col-6">
                        <strong>Date:</strong><br />
                        {selectedPrescription.datePrescribed}
                      </div>
                    </div>
                    <div className="row mt-3">
                      <div className="col-6">
                        <strong>Doctor:</strong><br />
                        {selectedPrescription.doctorName}
                      </div>
                      <div className="col-6">
                        <strong>Clinic:</strong><br />
                        {selectedPrescription.clinicName}
                      </div>
                    </div>
                    <div className="row mt-3">
                      <div className="col-6">
                        <strong>Condition:</strong><br />
                        {selectedPrescription.conditionName}
                      </div>
                      <div className="col-6">
                        <strong>Status:</strong><br />
                        <span className={`badge ${selectedPrescription.isActive ? 'bg-success' : 'bg-secondary'}`}>
                          {selectedPrescription.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card mb-3">
                  <div className="card-header">
                    <h6 className="mb-0">
                      <FiActivity className="me-2" />
                      Medicine Details
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-12">
                        <strong>Medicine:</strong><br />
                        {selectedPrescription.medicineName}
                      </div>
                    </div>
                    <div className="row mt-3">
                      <div className="col-4">
                        <strong>Dosage:</strong><br />
                        {selectedPrescription.dosage || 'N/A'}
                      </div>
                      <div className="col-4">
                        <strong>Frequency:</strong><br />
                        {selectedPrescription.frequency || 'N/A'}
                      </div>
                      <div className="col-4">
                        <strong>Duration:</strong><br />
                        {selectedPrescription.duration || 'N/A'}
                      </div>
                    </div>
                    <div className="row mt-3">
                      <div className="col-12">
                        <strong>Purpose:</strong><br />
                        {selectedPrescription.purpose || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card mb-3">
                  <div className="card-header">
                    <h6 className="mb-0">Clinical Information</h6>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <strong>Symptoms:</strong><br />
                      {selectedPrescription.symptoms || 'N/A'}
                    </div>
                    <div className="mb-3">
                      <strong>Suggestion:</strong><br />
                      {selectedPrescription.suggestion || 'N/A'}
                    </div>
                    <div className="mb-3">
                      <strong>Diseases:</strong><br />
                      {selectedPrescription.diseases || 'N/A'}
                    </div>
                    <div className="mb-3">
                      <strong>Lab Tests:</strong><br />
                      {selectedPrescription.labtests || 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="d-grid gap-2">
                  <button 
                    className="btn btn-outline-primary"
                    onClick={() => handleEditPrescription(selectedPrescription.id)}
                  >
                    <FiEdit3 className="me-2" />
                    Edit Prescription
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Offcanvas para añadir prescripción */}
{showAddOffcanvas && (
  <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{ width: '800px' }}>
    <div className="offcanvas-header">
      <h5 className="offcanvas-title">
        <FiPlus className="me-2" />
        Add New Prescription
      </h5>
      <button 
        type="button" 
        className="btn-close"
        onClick={handleCloseOffcanvas}
      ></button>
    </div>
    <div className="offcanvas-body">
      <PrescriptionForm 
        activePatient={patientData} // ADD THIS LINE
        onSubmit={handleFormSubmit}
        onCancel={handleCloseOffcanvas}
        isLoading={isSubmitting} // CHANGE FROM isSubmitting to isLoading
        mode="create" // ADD THIS LINE
      />
    </div>
  </div>
)}

      {/* Offcanvas para editar prescripción */}
{showEditOffcanvas && editingPrescription && (
  <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{ width: '800px' }}>
    <div className="offcanvas-header">
      <h5 className="offcanvas-title">
        <FiEdit3 className="me-2" />
        Edit Prescription
      </h5>
      <button 
        type="button" 
        className="btn-close"
        onClick={handleCloseOffcanvas}
      ></button>
    </div>
    <div className="offcanvas-body">
      <PrescriptionForm 
        initialData={editingPrescription} // CHANGE FROM prescription to initialData
        activePatient={patientData} // ADD THIS LINE
        onSubmit={handleFormSubmit}
        onCancel={handleCloseOffcanvas}
        isLoading={isSubmitting} // CHANGE FROM isSubmitting to isLoading
        mode="edit" // ADD THIS LINE
      />
    </div>
  </div>
)}

      {/* Alert Information */}
      {alertInfo && (
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1050 }}>
          <div className={`alert alert-${alertInfo.type} alert-dismissible fade show`}>
            {alertInfo.message}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setAlertInfo(null)}
            ></button>
          </div>
        </div>
      )}

      {/* Backdrop for offcanvas */}
      {(showFilterOffcanvas || showViewOffcanvas || showAddOffcanvas || showEditOffcanvas) && (
        <div 
          className="offcanvas-backdrop fade show"
          onClick={handleCloseOffcanvas}
        ></div>
      )}
      {/* Modal de confirmación para eliminar */}
{showDeleteConfirm && (
  <>
    <div className="modal show d-block" tabIndex="-1"style={{ zIndex: 1060 }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <FiAlertTriangle className="me-2 text-danger" />
              Confirm Delete
            </h5>
          </div>
          <div className="modal-body">
            <p>Are you sure you want to delete this prescription?</p>
            {prescriptionToDelete && (
              <div className="alert alert-info">
                <strong>Prescription ID:</strong> {prescriptionToDelete.id}<br/>
                <strong>Medicine:</strong> {prescriptionToDelete.medicineName}<br/>
                <strong>Doctor:</strong> {prescriptionToDelete.doctorName}
              </div>
            )}
            <p className="text-danger">
              <strong>This action cannot be undone.</strong>
            </p>
          </div>
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={cancelDeletePrescription}
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-danger"
              onClick={confirmDeletePrescription}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <FiTrash2 className="me-2" />
                  Delete
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div className="modal-backdrop show"></div>
  </>
)}

    </main>
  );
};

export default PatientPrescriptionTreatments;